import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle, Copy, RefreshCw, Loader2, AlertCircle, Clock } from 'lucide-react';

interface PixPaymentScreenProps {
  orderId: string;
  orderPaymentData: {
    id: string;
    pix_qr_code: string;
    pix_qr_code_base64: string;
    pix_expiration: string;
  };
  onSuccess: () => void;
  onGenerateNew: () => void;
  getPixPaymentStatus: (orderPaymentId: string) => Promise<any>;
}

const PixPaymentScreen: React.FC<PixPaymentScreenProps> = ({
  orderId,
  orderPaymentData,
  onSuccess,
  onGenerateNew,
  getPixPaymentStatus,
}) => {
  const [status, setStatus] = useState<'pending' | 'approved' | 'expired'>('pending');
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCalledSuccess = useRef(false);

  // Calculate remaining time from expiration
  const getTimeRemaining = useCallback(() => {
    const expiration = new Date(orderPaymentData.pix_expiration).getTime();
    const now = Date.now();
    const diff = expiration - now;

    if (diff <= 0) {
      return { expired: true, minutes: 0, seconds: 0 };
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { expired: false, minutes, seconds };
  }, [orderPaymentData.pix_expiration]);

  // Countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      const { expired, minutes, seconds } = getTimeRemaining();
      if (expired) {
        setStatus('expired');
        setCountdown('00:00');
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      } else {
        setCountdown(
          `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      }
    };

    updateCountdown();
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [getTimeRemaining]);

  // Poll payment status every 5 seconds
  useEffect(() => {
    if (status !== 'pending') return;

    const checkStatus = async () => {
      try {
        const response = await getPixPaymentStatus(orderPaymentData.id);
        if (response.status === 'approved' && !hasCalledSuccess.current) {
          setStatus('approved');
          hasCalledSuccess.current = true;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          // Delay before calling onSuccess to show the success animation
          setTimeout(() => {
            onSuccess();
          }, 3000);
        }
      } catch (error) {
        console.error('Erro ao verificar status do PIX:', error);
      }
    };

    // Initial check
    checkStatus();

    pollIntervalRef.current = setInterval(checkStatus, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [status, orderPaymentData.id, getPixPaymentStatus, onSuccess]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(orderPaymentData.pix_qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const handleGenerateNew = async () => {
    setIsGeneratingNew(true);
    try {
      await onGenerateNew();
      setStatus('pending');
      hasCalledSuccess.current = false;
    } finally {
      setIsGeneratingNew(false);
    }
  };

  // Approved state
  if (status === 'approved') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center animate-fade-in">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Pagamento confirmado!
          </h1>
          <p className="text-gray-600 mb-4">
            Seu pagamento via PIX foi aprovado com sucesso.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              Seu pedido esta sendo preparado!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Expired state
  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            PIX expirado
          </h1>
          <p className="text-gray-600 mb-6">
            O tempo para pagamento via PIX expirou. Gere um novo codigo para continuar.
          </p>
          <button
            onClick={handleGenerateNew}
            disabled={isGeneratingNew}
            className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--bg-color, #3b82f6)' }}
          >
            {isGeneratingNew ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Gerando novo PIX...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Gerar novo PIX
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Pending state - main payment screen
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Pagamento via PIX
          </h1>
          <p className="text-gray-600 text-sm">
            Escaneie o QR Code ou copie o codigo para pagar
          </p>
        </div>

        {/* QR Code Card */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* QR Code Image */}
          <div className="flex justify-center mb-6">
            <div className="bg-white p-3 rounded-lg border-2 border-gray-100">
              <img
                src={`data:image/png;base64,${orderPaymentData.pix_qr_code_base64}`}
                alt="QR Code PIX"
                className="w-56 h-56"
              />
            </div>
          </div>

          {/* Copy Button */}
          <button
            onClick={handleCopy}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 ${
              copied ? 'bg-green-500' : ''
            }`}
            style={!copied ? { backgroundColor: 'var(--bg-color, #3b82f6)' } : undefined}
          >
            {copied ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Codigo copiado!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Copiar codigo PIX
              </>
            )}
          </button>
        </div>

        {/* Timer & Status */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Countdown */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="text-sm text-gray-600">Expira em:</span>
            <span className="text-lg font-bold font-mono text-gray-900">
              {countdown}
            </span>
          </div>

          {/* Waiting indicator */}
          <div className="flex items-center justify-center gap-3 py-3 px-4 bg-blue-50 rounded-lg border border-blue-100">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            <span className="text-sm font-medium text-blue-700">
              Aguardando pagamento...
            </span>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Como pagar:
          </h3>
          <ol className="space-y-2 text-sm text-gray-600">
            <li className="flex gap-2">
              <span className="font-bold text-gray-900">1.</span>
              Abra o app do seu banco
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-gray-900">2.</span>
              Escolha pagar via PIX com QR Code ou copie e cole
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-gray-900">3.</span>
              Confirme o pagamento
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default PixPaymentScreen;
